<template>
  <div ref="root" part="base">
    <!-- eslint-disable vue/no-v-html -->
    <div v-if="svgNode" :class="classModifiers" class="sp-icon" v-html="svgNode"></div>
    <!-- eslint-enable vue/no-v-html -->

    <div v-else :class="classModifiers" class="sp-icon">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";
import { useSize } from "#ce/composables/useSize";
import { props as iconProps } from "./props.js";
import { useColorToCssProperty } from "#ce/composables/useColor";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useIconFetcher } from "./composables/useIconFetcher";
import { useSvgFillColor } from "./composables/useSvgFillColor";

const props = defineProps(iconProps);

const root = useTemplateRef("root");

const { isSmScreen, breakpointModifiers } = useBreakpoints();
const { sizeModifiers } = useSize(props);

// Responsive props: switch to sm variants on small screens
const activeName = computed(() => (isSmScreen.value && props.nameSm ? props.nameSm : props.name));
const activeFillColor = computed(() => (isSmScreen.value && props.fillColorSm ? props.fillColorSm : props.fillColor));

const { svgNode } = useIconFetcher(activeName, props);

useSvgFillColor(root, activeFillColor);
useColorToCssProperty(activeFillColor, { cssProperty: "fill" });

const classNames = computed(() => ({
  [`icon--${activeName.value}`]: activeName.value,
}));

const classModifiers = computed(() => [classNames.value, ...sizeModifiers.value, ...breakpointModifiers.value]);
</script>

<style>
:host {
  --fill: var(--sp-comp-icon-fill, var(--sp-sys-color-icon-filled, black));
  --size: var(--sp-comp-icon-size, var(--sp-sys-icon-size-medium, 2rem));
  --font-size: var(--sp-icon-font-size, 1rem);
  display: block;
  font-size: var(--font-size);
  color: var(--fill);
}

::slotted(svg),
svg {
  height: var(--size);
  width: var(--size);
  fill: var(--fill-color-prop, var(--fill));
  transform: var(--sp-comp-icon-transform, revert-layer);
}
</style>

<style lang="scss" scoped>
.sp-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: var(--sp-comp-icon-overflow, hidden);
  height: var(--size);
  width: var(--size);
  max-height: var(--size);
  max-width: var(--size);

  // Sizes
  @each $size in (micro xxx-small xx-small x-small small medium large x-large xx-large xxx-large display x-display) {
    $size-value: var(--sp-sys-icon-size-#{$size}, 2rem);
    &.--size-#{$size} {
      --size: #{$size-value};
    }
    &.--size-sm-#{$size}.--is-sm-screen {
      --size: #{$size-value};
    }
    &.--size-md-#{$size}.--is-md-screen {
      --size: #{$size-value};
    }
  }
}

.icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  --icon-size: var(--size);
}
</style>
